import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  GitFork,
  Lock,
  RefreshCw,
  RotateCcw,
  Save,
  Trash2,
  Wand2,
  X,
} from "lucide-react";
import {
  rulesGetRule,
  rulesGetRuleHistory,
  rulesCreateRuleDraft,
  rulesUpdateRule,
  rulesActivateRule,
  rulesSupersedeRule,
  rulesRevertRule,
  rulesDeprecateRule,
  rulesCompactRule,
  rulesCompactRuleInstruction,
} from "../gen";
import type { RuleResponse } from "../gen/models";
import { DiffViewer } from "../components/DiffViewer";
import { useToast } from "../context/ToastContext";
import { RuleHistoryLineage } from "../components/rule/RuleHistoryLineage";
import { RuleRevertModal } from "../components/rule/RuleRevertModal";
import { UnifiedRevisionModal } from "../components/common/UnifiedRevisionModal";
import { ConfirmModal } from "../components/ConfirmModal";
import { TimeAgo } from "../components/common/TimeAgo";
import { useAgentOptions } from "../hooks/useAgentOptions";

export function RuleEditor() {
  const { rule_id } = useParams<{ rule_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const isNew = !rule_id || rule_id === "new";
  const agentOptions = useAgentOptions({ includeUniversal: true });

  const [rule, setRule] = useState<RuleResponse | null>(null);
  const [history, setHistory] = useState<RuleResponse[]>([]);
  const [selectedDiffRev, setSelectedDiffRev] = useState<RuleResponse | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfirmActivateOpen, setIsConfirmActivateOpen] = useState(false);
  const [isConfirmDeprecateOpen, setIsConfirmDeprecateOpen] = useState(false);

  // Form Fields
  const [title, setTitle] = useState("");
  const [agentType, setAgentType] = useState<string>("universal");
  const [category, setCategory] = useState<string>("safety");
  const [priority, setPriority] = useState<number>(100);
  const [instruction, setInstruction] = useState("");
  const [isPersonal, setIsPersonal] = useState(false);

  // Supersede Modal / Form state
  const [isSuperseding, setIsSuperseding] = useState(false);
  const [supersedeTitle, setSupersedeTitle] = useState("");
  const [supersedeCategory, setSupersedeCategory] = useState("safety");
  const [supersedePriority, setSupersedePriority] = useState<number>(100);
  const [supersedeInstruction, setSupersedeInstruction] = useState("");
  const [supersedeIsPersonal, setSupersedeIsPersonal] = useState(false);

  // Compaction State
  const [isCompactModalOpen, setIsCompactModalOpen] = useState(false);
  const [compactProposal, setCompactProposal] = useState<string | null>(null);
  const [isCompacting, setIsCompacting] = useState(false);
  const [compactOriginalChars, setCompactOriginalChars] = useState(0);
  const [compactResultChars, setCompactResultChars] = useState(0);

  // Revert Modal / state
  const [revertTargetRev, setRevertTargetRev] = useState<RuleResponse | null>(null);
  const [reverting, setReverting] = useState(false);

  const loadRuleAndHistory = async () => {
    if (isNew || !rule_id) return;
    setLoading(true);
    setError(null);
    try {
      const [rRes, hRes] = await Promise.all([
        rulesGetRule(rule_id),
        rulesGetRuleHistory(rule_id).catch(() => ({ data: [] })),
      ]);
      const currentRule = (rRes as any).data as RuleResponse;
      setRule(currentRule);
      setTitle(currentRule.title);
      setAgentType(currentRule.agent_type || "universal");
      setCategory(currentRule.category);
      setPriority(currentRule.priority);
      setInstruction(currentRule.instruction);
      setIsPersonal(Boolean(currentRule.user_id || currentRule.is_personal));

      const historyList = (((hRes as any).data || []) as RuleResponse[]);
      setHistory(historyList);
      // Select previous version for diff if available
      const prev = historyList.find((h) => h.id === currentRule.supersedes_rule_id);
      if (prev) setSelectedDiffRev(prev);
    } catch (err: any) {
      setError(err?.message || "Failed to load rule");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isNew) {
      loadRuleAndHistory();
    }
  }, [rule_id, isNew]);

  const handleSaveDraft = async () => {
    if (!title.trim() || !instruction.trim()) {
      showToast("Title and instruction are required.", { type: "warning" });
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const res = await rulesCreateRuleDraft({
          title: title.trim(),
          agent_type: agentType === "universal" ? undefined : agentType,
          category,
          priority,
          instruction: instruction.trim(),
          version: 1,
          is_personal: isPersonal,
        });
        showToast(isPersonal ? "Personal rule draft created" : "Rule draft created", { type: "success" });
        navigate(`/rules/${(res as any).data.id}${location.search}`);
      } else if (rule) {
        await rulesUpdateRule(rule.id, {
          title: title.trim(),
          agent_type: agentType === "universal" ? undefined : agentType,
          category,
          priority,
          instruction: instruction.trim(),
          is_personal: isPersonal,
        });
        loadRuleAndHistory();
        showToast("Rule saved successfully.", { type: "success" });
      }
    } catch (err: any) {
      showToast("Failed to save rule: " + err.message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    if (!rule) return;

    setSaving(true);
    try {
      await rulesActivateRule(rule.id);
      showToast("Rule directive activated", { type: "success" });
      loadRuleAndHistory();
    } catch (err: any) {
      showToast("Failed to activate rule: " + err.message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenSupersede = () => {
    if (!rule) return;
    setSupersedeTitle(`${rule.title} (v${rule.version + 1})`);
    setSupersedeCategory(rule.category);
    setSupersedePriority(rule.priority);
    setSupersedeInstruction(rule.instruction);
    setSupersedeIsPersonal(Boolean(rule.user_id || rule.is_personal));
    setIsSuperseding(true);
  };

  const handleExecuteSupersede = async () => {
    if (!rule) return;
    if (!supersedeTitle.trim() || !supersedeInstruction.trim()) {
      showToast("Title and instruction are required.", { type: "warning" });
      return;
    }
    setSaving(true);
    try {
      const res = await rulesSupersedeRule(rule.id, {
        title: supersedeTitle.trim(),
        instruction: supersedeInstruction.trim(),
        category: supersedeCategory,
        priority: Number(supersedePriority),
        is_personal: supersedeIsPersonal,
      });
      setIsSuperseding(false);
      showToast("Rule superseded with new version", { type: "success" });
      navigate(`/rules/${(res as any).data.id}${location.search}`);
    } catch (err: any) {
      showToast("Failed to supersede rule: " + err.message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeprecate = async () => {
    if (!rule) return;

    setSaving(true);
    try {
      await rulesDeprecateRule(rule.id);
      showToast("Rule deprecated", { type: "info" });
      loadRuleAndHistory();
    } catch (err: any) {
      showToast("Failed to deprecate rule: " + err.message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const activeRule = history.find((h) => h.status === "active") || (rule?.status === "active" ? rule : null);
  const maxVersion = history.reduce((max, h) => Math.max(max, h.version), rule?.version || 1);
  const nextVersionNumber = maxVersion + 1;

  const handleExecuteRevert = async () => {
    if (!revertTargetRev) return;
    setReverting(true);
    try {
      const res = await rulesRevertRule(revertTargetRev.id);
      const newRule = (res as any).data as RuleResponse;
      setRevertTargetRev(null);
      showToast(`Reverted to Version ${revertTargetRev.version}. Version ${newRule.version} activated.`, { type: "success" });
      navigate(`/rules/${newRule.id}${location.search}`);
    } catch (err: any) {
      showToast("Failed to revert rule: " + (err?.message || "Unknown error"), { type: "error" });
    } finally {
      setReverting(false);
    }
  };

  const currentInstructionText = isNew || rule?.status === "draft" ? instruction : (rule?.instruction || instruction);
  const currentInstructionLength = currentInstructionText.length;

  const handleOpenCompactModal = async () => {
    if (!currentInstructionText.trim()) {
      showToast("No instruction text to compact.", { type: "warning" });
      return;
    }
    setCompactOriginalChars(currentInstructionText.length);
    setCompactProposal(null);
    setIsCompactModalOpen(true);
    await handleRunCompaction(currentInstructionText);
  };

  const handleRunCompaction = async (text?: string) => {
    const textToCompact = text || currentInstructionText;
    setIsCompacting(true);
    try {
      if (!isNew && rule) {
        const res = await rulesCompactRule(rule.id, { create_draft: false });
        const data = (res as any).data;
        setCompactProposal(data.compacted_instruction);
        setCompactOriginalChars(data.original_chars);
        setCompactResultChars(data.compacted_chars);
      } else {
        const res = await rulesCompactRuleInstruction({
          title: title || "Directive",
          instruction: textToCompact,
          target_chars: 1000,
        });
        const data = (res as any).data;
        setCompactProposal(data.compacted_instruction);
        setCompactOriginalChars(data.original_chars);
        setCompactResultChars(data.compacted_chars);
      }
    } catch (err: any) {
      showToast("Compaction failed: " + (err?.message || "Unknown error"), { type: "error" });
    } finally {
      setIsCompacting(false);
    }
  };

  const handleCreateDraftFromCompacted = async () => {
    if (!compactProposal) return;
    setSaving(true);
    try {
      if (!isNew && rule) {
        const res = await rulesCreateRuleDraft({
          title: rule.title,
          agent_type: rule.agent_type || undefined,
          category: rule.category,
          priority: rule.priority,
          instruction: compactProposal,
          version: nextVersionNumber,
          supersedes_rule_id: rule.id,
          is_personal: isPersonal,
        });
        setIsCompactModalOpen(false);
        showToast("Created compacted draft revision (vNext)", { type: "success" });
        navigate(`/rules/${(res as any).data.id}${location.search}`);
      } else {
        setInstruction(compactProposal);
        setIsCompactModalOpen(false);
        showToast("Applied compacted directive to editor", { type: "success" });
      }
    } catch (err: any) {
      showToast("Failed to create draft revision: " + (err?.message || "Unknown error"), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleApplyCompactedToForm = () => {
    if (!compactProposal) return;
    setInstruction(compactProposal);
    setIsCompactModalOpen(false);
    showToast("Applied compacted directive to editor", { type: "info" });
  };


  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-12 text-center text-stone-400 dark:text-stone-500 text-xs">
        Loading operational rule details...
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-12 text-center text-rose-600 dark:text-rose-400 text-xs">
        {error}
      </div>
    );
  }

  const isRuleInactive = Boolean(!isNew && rule && rule.status !== "active");

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Top Header */}
      <div className="space-y-4">
        <div className="md:hidden pb-1">
          <button
            type="button"
            onClick={() => navigate(`/rules${location.search}`)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Back to directives</span>
          </button>
        </div>
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-2xs flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1
                className={`text-xl font-bold tracking-tight text-stone-900 dark:text-stone-100 ${
                  isRuleInactive ? "line-through opacity-70" : ""
                }`}
              >
                {isNew ? "Draft New Operational Directive" : title || "Rule Specification"}
              </h1>
              {!isNew && rule && (
                <>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-mono font-bold bg-stone-100 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 ${
                      isRuleInactive
                        ? "line-through text-stone-400 dark:text-stone-500"
                        : "text-stone-700 dark:text-stone-300"
                    }`}
                  >
                    v{rule.version}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize border ${
                      rule.status === "active"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800/80"
                        : rule.status === "draft"
                        ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-800/80"
                        : rule.status === "superseded"
                        ? "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700"
                        : "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-400 dark:border-rose-800/80"
                    }`}
                  >
                    {rule.status}
                  </span>
                  {rule.is_personal || rule.user_id ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-950/60 dark:text-purple-300 dark:border-purple-800/80">
                      <Lock className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                      <span>Personal / Private</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-stone-100 text-stone-600 border border-stone-200 dark:bg-stone-800 dark:text-stone-300 dark:border-stone-700">
                      <span>Universal Org</span>
                    </span>
                  )}
                  {rule.reverted_from_version && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-800/80"
                      title={`This revision was created by reverting to Version ${rule.reverted_from_version}`}
                    >
                      <RotateCcw className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                      <span>Reverted from v{rule.reverted_from_version}</span>
                    </span>
                  )}
                </>
              )}
            </div>
            {isNew ? (
              <p className="text-stone-500 dark:text-stone-400 text-xs">
                Draft a new directive to guide agent behavior during conversational turns.
              </p>
            ) : rule ? (
              <div className="flex flex-wrap items-center gap-3 text-xs text-stone-400 dark:text-stone-500 pt-1">
                <span>ID: <code className="font-mono text-stone-600 dark:text-stone-400">{rule.id}</code></span>
                <span>•</span>
                <span>
                  Created by: <strong className="text-stone-700 dark:text-stone-300">{rule.created_by_name || rule.created_by || "Operator"}</strong>
                </span>
                <span>•</span>
                <span>
                  Created: <TimeAgo date={rule.created_at} className="text-stone-700 dark:text-stone-300 font-medium" />
                </span>
                {rule.updated_at && rule.updated_at !== rule.created_at && (
                  <>
                    <span>•</span>
                    <span>
                      Updated: <TimeAgo date={rule.updated_at} titlePrefix="Updated: " className="text-stone-700 dark:text-stone-300 font-medium" />
                    </span>
                  </>
                )}
              </div>
            ) : null}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {(isNew || rule?.status === "draft") && (
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs disabled:opacity-50 cursor-pointer"
              >
                <Save className="w-3.5 h-3.5" />
                <span>{isNew ? "Create Draft" : "Save Draft"}</span>
              </button>
            )}

            {!isNew && rule?.status === "draft" && (
              <button
                type="button"
                onClick={() => setIsConfirmActivateOpen(true)}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 transition shadow-2xs disabled:opacity-50 cursor-pointer"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Activate Rule</span>
              </button>
            )}

            {!isNew && rule && rule.status !== "active" && rule.status !== "draft" && (
              <button
                type="button"
                onClick={() => setRevertTargetRev(rule)}
                disabled={saving || reverting}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 transition shadow-2xs disabled:opacity-50 cursor-pointer"
                title={`Make Version ${rule.version} current`}
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Make Current</span>
              </button>
            )}

            {!isNew && rule?.status === "active" && (
              <>
                <button
                  type="button"
                  onClick={handleOpenSupersede}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-xs font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition shadow-2xs disabled:opacity-50"
                >
                  <GitFork className="w-3.5 h-3.5" />
                  <span>Supersede (vNext)</span>
                </button>

                <button
                  type="button"
                  onClick={() => setIsConfirmDeprecateOpen(true)}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-rose-200 dark:border-rose-800/80 bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-400 text-xs font-medium hover:bg-rose-100 dark:hover:bg-rose-900/60 transition shadow-2xs disabled:opacity-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Deprecate</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main 2-Column Editor Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left 2 Cols: Form Editor */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-2xs space-y-5">
            {/* Title Input or Read-Only Display */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300">
                Rule Title
              </label>
              {isNew || rule?.status === "draft" ? (
                <input
                  type="text"
                  placeholder="e.g. Universal Ocean & Surf Safety Directive"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2.5 text-xs text-stone-900 dark:text-stone-100 font-medium placeholder:text-stone-400 dark:placeholder:text-stone-500 focus:border-stone-500 dark:focus:border-stone-400 focus:outline-hidden"
                />
              ) : (
                <div className="text-sm font-semibold text-stone-900 dark:text-stone-100">
                  {rule?.title}
                </div>
              )}
            </div>

            {/* Scope, Category & Priority Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Agent / Scope Selector */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300">
                  Target Scope
                </label>
                {isNew || rule?.status === "draft" ? (
                  <select
                    value={agentType}
                    onChange={(e) => setAgentType(e.target.value)}
                    className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2.5 text-xs text-stone-800 dark:text-stone-200 font-medium focus:border-stone-500 dark:focus:border-stone-400 focus:outline-hidden"
                  >
                    {agentOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="inline-block px-2.5 py-1 rounded text-xs font-medium bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 capitalize">
                    {agentOptions.find((a) => a.value === (rule?.agent_type || "universal"))?.label || rule?.agent_type || "Universal (All Agents)"}
                  </span>
                )}
              </div>

              {/* Category Selector */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300">
                  Category
                </label>
                {isNew || rule?.status === "draft" ? (
                  <div className="relative">
                    <input
                      type="text"
                      list="rule-categories"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      placeholder="Select or enter category..."
                      className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2.5 text-xs text-stone-800 dark:text-stone-200 font-medium focus:border-stone-500 dark:focus:border-stone-400 focus:outline-hidden"
                    />
                    <datalist id="rule-categories">
                      <option value="safety">Safety Directives</option>
                      <option value="pricing">Pricing & Incentives</option>
                      <option value="logistics">Logistics & Transfers</option>
                      <option value="cancellation">Cancellation Policies</option>
                      <option value="brand">Brand Tone & Voice</option>
                      <option value="feedback">Feedback & Curation</option>
                      <option value="evaluator">Evaluator Directives</option>
                      <option value="etiquette">Staff Etiquette</option>
                      <option value="operations">General Operations</option>
                    </datalist>
                  </div>
                ) : (
                  <span className="inline-block px-2.5 py-1 rounded text-xs font-medium bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 capitalize">
                    {rule?.category}
                  </span>
                )}
              </div>

              {/* Priority Slider */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300">
                    Priority
                  </label>
                  <span className="font-mono text-xs font-bold text-stone-900 dark:text-stone-100 bg-stone-100 dark:bg-stone-800 px-2 py-0.5 rounded">
                    {isNew || rule?.status === "draft" ? priority : rule?.priority}
                  </span>
                </div>
                {isNew || rule?.status === "draft" ? (
                  <>
                    <input
                      type="range"
                      min="0"
                      max="300"
                      step="10"
                      value={priority}
                      onChange={(e) => setPriority(Number(e.target.value))}
                      className="w-full accent-stone-900 dark:accent-stone-100 h-2 bg-stone-200 dark:bg-stone-800 rounded-lg cursor-pointer"
                    />
                    <div className="flex justify-between text-[10px] text-stone-400 dark:text-stone-500">
                      <span>0 (Low)</span>
                      <span>150 (Med)</span>
                      <span>300 (Critical)</span>
                    </div>
                  </>
                ) : null}
              </div>
            </div>

            {/* Personal / Private Rule Toggle */}
            <div className="flex items-center justify-between p-3.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/50">
              <div className="space-y-0.5 pr-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300">
                    Personal / Private Rule
                  </span>
                  {isPersonal && (
                    <span className="px-1.5 py-0.2 rounded text-[10px] font-semibold bg-purple-100 text-purple-800 dark:bg-purple-950/80 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                      Private to You
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-stone-500 dark:text-stone-400">
                  Attach this directive to your user account. Visible only to you, used for personal email tone of voice and individual style preferences.
                </p>
              </div>
              {isNew || rule?.status === "draft" ? (
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={isPersonal}
                    onChange={(e) => setIsPersonal(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-stone-200 peer-focus:outline-none rounded-full peer dark:bg-stone-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-stone-600 peer-checked:bg-purple-600"></div>
                </label>
              ) : (
                <span className={`text-xs font-semibold px-2 py-1 rounded border ${
                  rule?.user_id || rule?.is_personal
                    ? "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/60 dark:text-purple-300 dark:border-purple-800"
                    : "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700"
                }`}>
                  {rule?.user_id || rule?.is_personal ? "Private (Attached to user)" : "Org Universal"}
                </span>
              )}
            </div>

            {/* Instruction Body */}
            <div className="space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300">
                    Instruction Text (Directives & Invariants)
                  </label>
                  {currentInstructionLength > 1000 && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950/70 dark:text-amber-300 dark:border-amber-800 cursor-help shadow-2xs"
                      title="Long directive (>1,000 chars) increases prompt token consumption, adds turn latency, and may dilute model attention across operational constraints."
                    >
                      <AlertTriangle className="w-2.5 h-2.5 text-amber-600 dark:text-amber-400 shrink-0" />
                      <span>{currentInstructionLength.toLocaleString()} chars (&gt;1,000 char threshold)</span>
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={handleOpenCompactModal}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300/80 dark:bg-amber-950/50 dark:hover:bg-amber-900/60 dark:text-amber-200 dark:border-amber-800 transition cursor-pointer"
                    title="Propose AI compaction to compress directive under 1,000 chars and create a draft revision"
                  >
                    <Wand2 className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                    <span>Compact Directive</span>
                  </button>
                  <span className="text-[11px] text-stone-400 dark:text-stone-500">
                    {isNew || rule?.status === "draft"
                      ? "Included in active agent system prompts"
                      : "Read-only • Use 'Supersede (vNext)' to create a new revision"}
                  </span>
                </div>
              </div>

              {currentInstructionLength > 1000 && (
                <div className="flex items-center justify-between p-3 rounded-lg border border-amber-300 dark:border-amber-700/80 bg-amber-50/80 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 text-xs">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                    <div>
                      <span className="font-bold">Directive is too long:</span>{" "}
                      <span className="font-mono font-bold">{currentInstructionLength.toLocaleString()}</span> characters (exceeds recommended 1,000 char threshold). Long directives increase prompt token costs, add turn latency, and dilute model attention.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenCompactModal}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs shadow-2xs transition shrink-0 ml-3 cursor-pointer"
                  >
                    <Wand2 className="w-3.5 h-3.5" />
                    <span>Compact Now</span>
                  </button>
                </div>
              )}

              {isNew || rule?.status === "draft" ? (
                <textarea
                  rows={6}
                  placeholder="Explicit directive instructions provided to agent prompt..."
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  className={`w-full rounded-lg border ${
                    currentInstructionLength > 1000
                      ? "border-amber-400 dark:border-amber-600 bg-amber-50/20 dark:bg-amber-950/20 ring-1 ring-amber-400/40 focus:border-amber-500"
                      : "border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 focus:border-stone-500 dark:focus:border-stone-400"
                  } p-3 font-mono text-xs text-stone-900 dark:text-stone-100 leading-relaxed placeholder:text-stone-400 dark:placeholder:text-stone-500 focus:outline-hidden`}
                />
              ) : (
                <div
                  className={`rounded-lg border ${
                    currentInstructionLength > 1000
                      ? "border-amber-300 dark:border-amber-700/80 bg-amber-50/40 dark:bg-amber-950/30 ring-1 ring-amber-400/30 text-stone-900 dark:text-stone-100"
                      : "border-stone-200 dark:border-stone-800 bg-stone-50/70 dark:bg-stone-950/70 text-stone-800 dark:text-stone-200"
                  } p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap select-text`}
                >
                  {rule?.instruction}
                </div>
              )}
            </div>
          </div>

          {/* Side-by-Side Diff Viewer (if revision selected) */}
          {selectedDiffRev && (
            <div className="space-y-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-stone-600 dark:text-stone-400">
                Revision Comparison (Side-by-Side Diff)
              </h3>
              <DiffViewer
                oldTitle={`v${selectedDiffRev.version}: ${selectedDiffRev.title}`}
                newTitle={`v${rule?.version || 1}: ${title}`}
                oldText={selectedDiffRev.instruction}
                newText={instruction}
              />
            </div>
          )}
        </div>

        {/* Right 1 Col: Revision History & Lineage Tree */}
        <div className="space-y-6">
          <RuleHistoryLineage
            history={history}
            currentRule={rule}
            selectedDiffRev={selectedDiffRev}
            onSelectDiffRev={setSelectedDiffRev}
            onRequestRevert={setRevertTargetRev}
          />
        </div>
      </div>

      {/* Rule Revert Confirmation Dialog */}
      <RuleRevertModal
        isOpen={Boolean(revertTargetRev)}
        onClose={() => setRevertTargetRev(null)}
        onConfirm={handleExecuteRevert}
        targetRule={revertTargetRev}
        activeRule={activeRule}
        nextVersionNumber={nextVersionNumber}
        loading={reverting}
      />

      {/* Unified Revision Modal for Superseding */}
      <UnifiedRevisionModal
        isOpen={isSuperseding}
        onClose={() => setIsSuperseding(false)}
        title={`Create New Rule Version (v${(rule?.version || 1) + 1})`}
        subtitle="Rules are immutable. Creating this revision will supersede the current version and immediately activate vNext."
        fields={[
          {
            label: "Revision Title",
            type: "text",
            value: supersedeTitle,
            onChange: setSupersedeTitle,
            placeholder: "Revision title...",
          },
          {
            label: "Category",
            type: "select",
            value: supersedeCategory,
            onChange: setSupersedeCategory,
            options: [
              ...(rule?.category ? [{ value: rule.category, label: rule.category }] : []),
              { value: "safety", label: "Safety Directives" },
              { value: "pricing", label: "Pricing & Incentives" },
              { value: "logistics", label: "Logistics & Transfers" },
              { value: "cancellation", label: "Cancellation Policies" },
              { value: "brand", label: "Brand Tone & Voice" },
              { value: "feedback", label: "Feedback & Curation" },
              { value: "evaluator", label: "Evaluator Directives" },
              { value: "etiquette", label: "Staff Etiquette" },
              { value: "operations", label: "General Operations" },
            ].filter((v, i, a) => a.findIndex((t) => t.value === v.value) === i),
          },
          {
            label: "Personal Rule",
            type: "checkbox",
            value: supersedeIsPersonal,
            onChange: setSupersedeIsPersonal,
            description: "Personal / Private directive (visible only to you, for email tone of voice and style)",
          },
        ]}
        contentLabel={
          supersedeInstruction.length > 1000
            ? `Updated Instruction Text (${supersedeInstruction.length.toLocaleString()} chars — Advisory: Keep under 1,000 chars)`
            : "Updated Instruction Text (Directives & Invariants)"
        }
        contentValue={supersedeInstruction}
        onChangeContent={setSupersedeInstruction}
        contentRows={6}
        oldTitle={`v${rule?.version || 1} Current`}
        newTitle={`v${(rule?.version || 1) + 1} New Version`}
        oldContent={rule?.instruction || ""}
        newContent={supersedeInstruction}
        confirmLabel="Confirm & Activate vNext"
        onConfirm={handleExecuteSupersede}
        submitting={saving}
      />

      {/* Activate Rule Confirmation Modal */}
      <ConfirmModal
        isOpen={isConfirmActivateOpen}
        onClose={() => !saving && setIsConfirmActivateOpen(false)}
        onConfirm={async () => {
          await handleActivate();
          setIsConfirmActivateOpen(false);
        }}
        title="Activate Rule Directive"
        description="Are you sure you want to activate this rule directive?"
        confirmText="Activate"
        loadingText="Activating..."
        variant="primary"
        isLoading={saving}
      />

      {/* Deprecate Rule Confirmation Modal */}
      <ConfirmModal
        isOpen={isConfirmDeprecateOpen}
        onClose={() => !saving && setIsConfirmDeprecateOpen(false)}
        onConfirm={async () => {
          await handleDeprecate();
          setIsConfirmDeprecateOpen(false);
        }}
        title="Deprecate Rule"
        description="Are you sure you want to deprecate this rule? It will no longer be included in dynamic agent prompts."
        confirmText="Deprecate"
        loadingText="Deprecating..."
        variant="danger"
        isLoading={saving}
      />

      {/* Compaction Proposal Modal */}
      {isCompactModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 max-w-4xl w-full p-6 shadow-2xl space-y-5 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between pb-3 border-b border-stone-100 dark:border-stone-800">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Wand2 className="w-4 h-4 text-amber-500" />
                  <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100">
                    Compact Directive Proposal
                  </h3>
                  {compactOriginalChars > 1000 && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 dark:bg-amber-950/70 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
                      {compactOriginalChars.toLocaleString()} chars
                    </span>
                  )}
                </div>
                <p className="text-xs text-stone-500 dark:text-stone-400">
                  Compresses rule directives to under 1,000 characters while strictly preserving operational invariants, numbers, and policies.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsCompactModalOpen(false)}
                className="p-1.5 rounded-lg text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto space-y-4">
              {isCompacting ? (
                <div className="py-16 text-center space-y-3">
                  <RefreshCw className="w-6 h-6 animate-spin text-amber-500 mx-auto" />
                  <div className="text-xs font-semibold text-stone-700 dark:text-stone-300">
                    Compacting directive using model...
                  </div>
                  <p className="text-[11px] text-stone-400 dark:text-stone-500 max-w-md mx-auto">
                    Analyzing constraints, eliminating rhetorical filler, and keeping instructions under 1,000 characters.
                  </p>
                </div>
              ) : compactProposal ? (
                <div className="space-y-4">
                  {/* Delta Stats Bar */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 text-xs font-mono">
                    <div className="flex items-center gap-4">
                      <span>
                        Original: <strong className="text-stone-900 dark:text-stone-100">{compactOriginalChars.toLocaleString()}</strong> chars
                      </span>
                      <span>→</span>
                      <span>
                        Compacted: <strong className={`font-bold ${compactResultChars <= 1000 ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>{compactResultChars.toLocaleString()}</strong> chars
                      </span>
                    </div>
                    {compactOriginalChars > 0 && (
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                        {Math.round(((compactResultChars - compactOriginalChars) / compactOriginalChars) * 100)}% char delta
                      </span>
                    )}
                  </div>

                  {/* Side-by-side diff */}
                  <div className="space-y-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-stone-600 dark:text-stone-400">
                      Proposed Changeset Comparison
                    </div>
                    <DiffViewer
                      oldTitle={`Original Directive (${compactOriginalChars} chars)`}
                      newTitle={`Proposed Compacted Version (${compactResultChars} chars)`}
                      oldText={currentInstructionText}
                      newText={compactProposal}
                    />
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center space-y-3">
                  <p className="text-xs text-stone-500">Click below to generate a compacted proposal.</p>
                  <button
                    type="button"
                    onClick={() => handleRunCompaction()}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 transition"
                  >
                    <Wand2 className="w-3.5 h-3.5" />
                    <span>Generate Proposal</span>
                  </button>
                </div>
              )}
            </div>

            {/* Footer Buttons */}
            <div className="pt-3 border-t border-stone-100 dark:border-stone-800 flex items-center justify-between">
              <button
                type="button"
                onClick={() => handleRunCompaction()}
                disabled={isCompacting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700 text-xs font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${isCompacting ? "animate-spin" : ""}`} />
                <span>Re-generate</span>
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsCompactModalOpen(false)}
                  className="px-3.5 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700 text-xs font-medium text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition"
                >
                  Cancel
                </button>

                {Boolean(compactProposal) && (
                  <>
                    {(isNew || rule?.status === "draft") && (
                      <button
                        type="button"
                        onClick={handleApplyCompactedToForm}
                        className="px-3.5 py-1.5 rounded-lg border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-200 text-xs font-semibold hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition cursor-pointer"
                      >
                        Apply to Form
                      </button>
                    )}

                    {!isNew && rule && (
                      <button
                        type="button"
                        onClick={handleCreateDraftFromCompacted}
                        disabled={saving}
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 transition shadow-2xs disabled:opacity-50 cursor-pointer"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Create Draft Revision (v{nextVersionNumber})</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
